import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiArrowLeft, FiUsers, FiUserPlus, FiTrash2, FiMail, FiShield, FiSearch, FiCheck } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import {
  getDataRoomDetails,
  addDataRoomMember,
  updateMemberRole,
  removeMember,
  DataRoomDetails,
  DataRoomMember
} from '../services/dataRoomService';

interface PlatformUser {
  id: string;
  email: string;
  name: string | null;
  first_name: string | null;
  last_name: string | null;
}

const DataRoomMembers: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [details, setDetails] = useState<DataRoomDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newMemberRole, setNewMemberRole] = useState<'viewer' | 'uploader' | 'data_room_admin'>('viewer');
  const [addingMember, setAddingMember] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  
  // User selection
  const [platformUsers, setPlatformUsers] = useState<PlatformUser[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUser, setSelectedUser] = useState<PlatformUser | null>(null);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    if (id) loadDetails();
  }, [id]);

  useEffect(() => {
    if (showAddModal) {
      loadPlatformUsers();
    }
  }, [showAddModal]);

  const loadDetails = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const data = await getDataRoomDetails(id);
      if (!data.can_manage) {
        navigate(`/data-rooms/${id}`);
        return;
      }
      setDetails(data);
    } catch (err) {
      console.error('Error loading data room:', err);
      setError('Failed to load data room');
    } finally {
      setLoading(false);
    }
  };

  const loadPlatformUsers = async () => {
    if (!id) return;
    try {
      setLoadingUsers(true);
      // Use RPC function that bypasses RLS for data room admins
      const { data, error } = await supabase.rpc('get_users_for_data_room_invite', {
        p_data_room_id: id
      });
      
      if (error) throw error;
      
      if (data?.success && data?.users) {
        setPlatformUsers(data.users.map((u: any) => ({
          id: u.id,
          email: u.email || '',
          name: u.name,
          first_name: u.first_name,
          last_name: u.last_name
        })));
      } else {
        console.error('Failed to load users:', data?.error);
        setPlatformUsers([]);
      }
    } catch (err) {
      console.error('Error loading platform users:', err);
      setPlatformUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleAddMember = async () => {
    if (!id || !selectedUser) return;
    try {
      setAddingMember(true);
      setAddError(null);
      await addDataRoomMember(id, selectedUser.email, newMemberRole);
      setSelectedUser(null);
      setSearchQuery('');
      setNewMemberRole('viewer');
      setShowAddModal(false);
      await loadDetails();
    } catch (err: any) {
      console.error('Error adding member:', err);
      setAddError(err.message || 'Failed to add member');
    } finally {
      setAddingMember(false);
    }
  };

  const handleUpdateRole = async (member: DataRoomMember, newRole: 'viewer' | 'uploader' | 'data_room_admin') => {
    try {
      await updateMemberRole(member.id, newRole);
      await loadDetails();
    } catch (err) {
      console.error('Error updating role:', err);
    }
  };

  const handleRemoveMember = async (member: DataRoomMember) => {
    if (!confirm(`Remove ${member.email || member.name} from this data room?`)) return;
    try {
      await removeMember(member.id);
      await loadDetails();
    } catch (err) {
      console.error('Error removing member:', err);
    }
  };

  const getDisplayName = (u: PlatformUser) => {
    if (u.first_name && u.last_name) return `${u.first_name} ${u.last_name}`;
    if (u.name) return u.name;
    return u.email.split('@')[0];
  };

  const filteredUsers = platformUsers.filter(u => {
    const query = searchQuery.toLowerCase();
    return (
      u.email.toLowerCase().includes(query) ||
      (u.name?.toLowerCase() || '').includes(query) ||
      (u.first_name?.toLowerCase() || '').includes(query) ||
      (u.last_name?.toLowerCase() || '').includes(query)
    );
  });

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <p className="text-gray-400">Please log in to manage members</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !details) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error || 'Data room not found'}</p>
          <button onClick={() => navigate('/data-rooms')} className="text-cyan-400 hover:text-cyan-300">
            Back to Data Room
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 pt-20 pb-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <button
            onClick={() => navigate(`/data-rooms/${id}`)}
            className="flex items-center gap-2 text-gray-400 hover:text-white mb-4 transition-colors"
          >
            <FiArrowLeft className="w-4 h-4" />
            Back to Data Room
          </button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white flex items-center gap-3">
                <FiUsers className="text-cyan-400" />
                Manage Members
              </h1>
              <p className="text-gray-400 mt-1">{details.data_room.name}</p>
            </div>

            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-5 py-3 rounded-xl bg-gray-900/80 border border-cyan-500/20 hover:border-cyan-500/40 text-gray-300 hover:text-white font-medium transition-all"
            >
              <FiUserPlus className="w-5 h-5 text-cyan-400" />
              Add Member
            </button>
          </div>
        </motion.div>

        {/* Owner Section */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 p-5 rounded-2xl bg-gray-900/50 border border-purple-500/30"
        >
          <div className="flex items-center gap-3 mb-3">
            <FiShield className="w-5 h-5 text-purple-400" />
            <h3 className="text-sm font-medium text-purple-400">Data Room Owner</h3>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white">{details.data_room.owner_email}</p>
              <p className="text-xs text-gray-500">Full access to all content and settings</p>
            </div>
            <span className="px-3 py-1 rounded-full bg-purple-500/20 text-purple-400 text-sm">
              Owner
            </span>
          </div>
        </motion.div>

        {/* Members List */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="rounded-2xl bg-gray-900/50 border border-gray-800/50 overflow-hidden"
        >
          <div className="p-5 border-b border-gray-800/50">
            <h3 className="text-lg font-semibold text-white">
              Members ({details.members.length})
            </h3>
          </div>

          {details.members.length === 0 ? (
            <div className="p-12 text-center">
              <FiUsers className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500">No members yet</p>
              <p className="text-gray-600 text-sm mt-1">
                Add members to give them access to this data room
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-800/50">
              {details.members.map((member, index) => (
                <motion.div
                  key={member.id}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="p-5 flex items-center justify-between hover:bg-gray-800/20 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-gray-800 flex items-center justify-center">
                      <span className="text-gray-400 font-medium">
                        {(member.email || member.name || '?')[0].toUpperCase()}
                      </span>
                    </div>
                    <div>
                      <p className="text-white">{member.email || member.name}</p>
                      <p className="text-xs text-gray-500">
                        Added {new Date(member.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <select
                      value={member.role}
                      onChange={e => handleUpdateRole(member, e.target.value as any)}
                      className="px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm focus:border-cyan-500 focus:outline-none"
                    >
                      <option value="viewer">Viewer</option>
                      <option value="uploader">Uploader</option>
                      <option value="data_room_admin">Data Room Admin</option>
                    </select>

                    <button
                      onClick={() => handleRemoveMember(member)}
                      className="p-2 text-gray-400 hover:text-red-400 transition-colors"
                      title="Remove member"
                    >
                      <FiTrash2 className="w-5 h-5" />
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Role Descriptions */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mt-8 p-5 rounded-2xl bg-gray-900/30 border border-gray-800/30"
        >
          <h4 className="text-sm font-medium text-gray-400 mb-4">Role Permissions</h4>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-gray-300 font-medium mb-1">Viewer</p>
              <p className="text-gray-500">Can view and download files</p>
            </div>
            <div>
              <p className="text-blue-400 font-medium mb-1">Uploader</p>
              <p className="text-gray-500">Can view, download, and upload files</p>
            </div>
            <div>
              <p className="text-cyan-400 font-medium mb-1">Data Room Admin</p>
              <p className="text-gray-500">Can manage files, folders, and members</p>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Add Member Modal */}
      {showAddModal && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          onClick={() => setShowAddModal(false)}
        >
          <motion.div
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            onClick={e => e.stopPropagation()}
            className="w-full max-w-lg p-6 rounded-2xl bg-gray-900 border border-gray-800"
          >
            <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
              <FiUserPlus className="text-cyan-400" />
              Add New Member
            </h3>

            <div className="space-y-4">
              {/* User Search */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Select User
                </label>
                <div className="relative">
                  <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 w-5 h-5" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Search by name or email..."
                    className="w-full pl-12 pr-4 py-3 rounded-xl bg-gray-800 border border-gray-700 text-white placeholder-gray-500 focus:border-cyan-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* User List */}
              <div className="max-h-60 overflow-y-auto rounded-xl border border-gray-700 bg-gray-800/50">
                {loadingUsers ? (
                  <div className="p-4 text-center">
                    <div className="w-6 h-6 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin mx-auto" />
                  </div>
                ) : filteredUsers.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    {searchQuery ? 'No users found' : 'All users are already members'}
                  </div>
                ) : (
                  <div className="divide-y divide-gray-700/50">
                    {filteredUsers.map(u => (
                      <button
                        key={u.id}
                        onClick={() => setSelectedUser(u)}
                        className={`w-full p-3 flex items-center justify-between hover:bg-gray-700/30 transition-colors ${
                          selectedUser?.id === u.id ? 'bg-cyan-500/10 border-l-2 border-cyan-400' : ''
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center">
                            <span className="text-gray-300 text-sm font-medium">
                              {(u.email || '?')[0].toUpperCase()}
                            </span>
                          </div>
                          <div className="text-left">
                            <p className="text-white text-sm">{getDisplayName(u)}</p>
                            <p className="text-gray-500 text-xs">{u.email}</p>
                          </div>
                        </div>
                        {selectedUser?.id === u.id && (
                          <FiCheck className="w-5 h-5 text-cyan-400" />
                        )}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Role Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Role
                </label>
                <select
                  value={newMemberRole}
                  onChange={e => setNewMemberRole(e.target.value as any)}
                  className="w-full px-4 py-3 rounded-xl bg-gray-800 border border-gray-700 text-white focus:border-cyan-500 focus:outline-none"
                >
                  <option value="viewer">Viewer (read-only)</option>
                  <option value="uploader">Uploader (can upload files)</option>
                  <option value="data_room_admin">Data Room Admin (full access)</option>
                </select>
              </div>

              {addError && (
                <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                  {addError}
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setAddError(null);
                  setSelectedUser(null);
                  setSearchQuery('');
                }}
                className="px-4 py-2 rounded-lg text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleAddMember}
                disabled={addingMember || !selectedUser}
                className="px-5 py-2.5 rounded-xl bg-gray-900/80 border border-cyan-500/20 hover:border-cyan-500/40 text-gray-300 hover:text-white font-medium disabled:opacity-50 transition-all flex items-center gap-2"
              >
                {addingMember ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Adding...
                  </>
                ) : (
                  'Add Member'
                )}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
};

export default DataRoomMembers;